import { useCallback, useMemo, useState } from "react";
import CodeMirror from "@uiw/react-codemirror";
import { autocompletion, CompletionContext, CompletionResult } from "@codemirror/autocomplete";
import { BlockTypes } from "modules/MainSurveyEditor/helpers/blocks";
import { StreamLanguage } from "@codemirror/language";
import { HighlightStyle, syntaxHighlighting } from "@codemirror/language";
import { tags as t } from "@lezer/highlight";
import { EditorView } from "@codemirror/view";
import { existingBlocksClient } from "modules/ExistingBlocks/client/existingBlocksClient";
import { Typography } from "@coolset/cs-ui";

interface ExpressionInputV2Props {
  onChange: (value: { expression?: string; fields: string[] }) => void;
  value: { expression?: string; fields?: string[] };
  error?: string | null;
}

// Create a custom language for expressions
const expressionLanguage = StreamLanguage.define({
  name: "expression",
  token(stream) {
    if (stream.match("{")) return "brace";
    if (stream.match("}")) return "brace";
    if (stream.match(/[^{}]+(?=})/)) return "content";
    stream.next();
    return null;
  },
});

// Create a highlighting style
const highlightStyle = HighlightStyle.define([
  { tag: t.bracket, color: "#9c27b0" },
  { tag: t.variableName, color: "#2196f3" },
]);

export const ExpressionInputV2 = (props: ExpressionInputV2Props) => {
  const { onChange, value, error } = props;

  const [expression, setExpression] = useState(value.expression || "");

  const extractFieldIds = useCallback((expression: string = ""): string[] => {
    const matches = expression.match(/{([^}]+)}/g) || [];
    return matches.map((match) => match.slice(1, -1).trim());
  }, []);

  const completions = useCallback(async (context: CompletionContext): Promise<CompletionResult | null> => {
    const word = context.matchBefore(/{([^}]*)$/);
    if (!word) return null;

    const searchText = word.text.slice(1);

    try {
      const response = await existingBlocksClient.getExistingBlocks(searchText, [
        BlockTypes.Number,
        BlockTypes.Formula,
      ]);

      const options = response.data.map((block) => ({
        label: block.fieldId,
        type: block.contentType,
      }));

      return {
        from: word.from + 1,
        to: word.to,
        options,
        validFor: /^[^}]*$/,
      };
    } catch (error) {
      return null;
    }
  }, []);

  // CodeMirror extensions
  const extensions = useMemo(
    () => [
      expressionLanguage,
      syntaxHighlighting(highlightStyle),
      autocompletion({
        override: [completions],
      }),
      EditorView.lineWrapping,
      EditorView.theme({
        ".cm-scroller": {
          overflow: "hidden",
        },
      }),
    ],
    [completions]
  );

  const handleChange = useCallback(
    (expressionValue: string) => {
      setExpression(expressionValue);
      onChange({ expression: expressionValue, fields: extractFieldIds(expressionValue) });
    },
    [onChange, extractFieldIds]
  );

  return (
    <>
      <div className={`expression-input-container ${error ? 'has-error' : ''}`}>
        <CodeMirror
          value={expression}
          extensions={extensions}
          onChange={handleChange}
          placeholder="Enter expression... (Type { to start)"
          theme="light"
          basicSetup={{
            lineNumbers: false,
            foldGutter: false,
          }}
        />
      </div>
      {error && <Typography color="error">{error}</Typography>}
    </>
  );
};
